import { isBedbankItem } from 'lib/checkout/checkoutUtils'

/** The click-through fields svc-bedbank needs to honour Google's advertised price (GHA-553). */
export interface GhaPriceMatchParams {
  token: string
  roomId: string
  refundable: boolean
  /** Google's displayed tax and fees. Both or neither: svc-bedbank ignores one on its own. */
  sourceTax?: number
  sourceFees?: number
}

/** A price-matched room: the fields svc-bedbank needs, plus which of Google's rates it matched. */
export interface GhaPriceMatch extends GhaPriceMatchParams {
  /** Google advertised the members-only (CUG) rate, so svc-bedbank prices on its CUG session. */
  isCug: boolean
}

export interface GhaPriceMatchClickThrough extends GhaPriceMatch {
  propertyId: string
  /** GHA account the landing URL names (lpp, scoopon, ...); the rollout is decided per account. */
  accountType: string
  /** Google's displayed total and its currency, for logging how close our price came. */
  googlePrice?: number
  googleCurrency?: string
  /** The board Google advertised; known once svc-connection-google has approved the click. */
  boardCode?: string
}

/** Everything svc-connection-google's eligibility verdict depends on. */
export function ghaPriceMatchKey(
  { accountType, propertyId, token }: Pick<GhaPriceMatchClickThrough, 'accountType' | 'propertyId' | 'token'>,
): string {
  return `${accountType}|${propertyId}|${token}`
}

/**
 * The only fields svc-bedbank accepts inside `gha`: it rejects an enquiry carrying anything else.
 * Callers hold the wider match context, so every request that sends it picks here.
 */
export function pickGhaParams(gha: GhaPriceMatchParams): GhaPriceMatchParams {
  return { token: gha.token, roomId: gha.roomId, refundable: gha.refundable, sourceTax: gha.sourceTax, sourceFees: gha.sourceFees }
}

/** What a priced room keeps, so later re-enquiries ask svc-bedbank the same way. */
export function pickGhaPriceMatch(gha: GhaPriceMatch): GhaPriceMatch {
  return { ...pickGhaParams(gha), isCug: gha.isCug }
}

/**
 * Whether this property's room was booked from a price-matched click. Book now saves the price
 * match on the cart item, so this still answers on checkout, whose URL has no click-through.
 */
export function isGhaPriceMatchedInCart(items: ReadonlyArray<App.Checkout.AnyItem>, propertyId: string | undefined): boolean {
  return items.some(item => isBedbankItem(item) && item.offerId === propertyId && !!item.gha)
}

export function ghaQueryParams(gha: GhaPriceMatchParams | undefined) {
  return gha && {
    ghaToken: gha.token,
    ghaRoomId: gha.roomId,
    ghaRefundable: gha.refundable,
    // svc-accommodation's contract types these query params as strings.
    ghaSourceTax: gha.sourceTax?.toString(),
    ghaSourceFees: gha.sourceFees?.toString(),
  }
}

// svc-connection-google writes Google's PackageID as bf?_dn?_int?_park?_ref?_S<supplier>_pkg;
// the ref digit is the refundability of the advertised rate.
const GHA_PACKAGE_REFUNDABLE_PATTERN = /_ref([01])_/

export function parseGhaPackageRefundable(roomRateId: string | undefined): boolean | undefined {
  const digit = roomRateId ? GHA_PACKAGE_REFUNDABLE_PATTERN.exec(roomRateId)?.[1] : undefined
  if (digit === undefined) return undefined
  return digit === '1'
}

export function parseGhaRefundableParam(value: string | undefined): boolean | undefined {
  if (value === 'true') return true
  if (value === 'false') return false
  return undefined
}
